import {
  FaroErrorBoundary,
  withFaroRouterInstrumentation,
} from "@grafana/faro-react";
import * as Sentry from "@sentry/react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { FlagProvider, useFlagsStatus } from "@unleash/proxy-client-react";
import * as React from "react";
import { I18nProvider } from "react-aria-components";
import { createRoot } from "react-dom/client";
import {
  type NavigateOptions,
  type RouteObject,
  createBrowserRouter,
  createHashRouter,
} from "react-router";
import { RouterProvider } from "react-router/dom";
import { NavigationBlocker } from "@/shared/queries/networkState.ts";
import type { Router } from "@/shared/types.ts";
import { TimeoutWatcher } from "@/shared/ui/legacy/TimeoutWatcher.tsx";
import { QueryClientProvider } from "@/shared/util/fetch.tsx";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";
import { invariant } from "@/shared/util/invariant.ts";
import { I18n } from "@/shared/util/translations.ts";

declare module "react-aria-components" {
  interface RouterConfig {
    routerOptions: NavigateOptions;
  }
}

const NothingUntilUnleashReady: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const status = useFlagsStatus();

  if (status.flagsReady || status.flagsError) {
    return children;
  }

  return null;
};

const isDevelopment = process.env.NODE_ENV === "development";

const languages: Record<string, string> = {
  dk: "da-DK",
  de: "de-DE",
  en: "en-GB",
  fi: "fi-FI",
  fr: "fr-FR",
  no: "nb-NO",
  se: "sv-SE",
};

const locale = languages[I18n.locale] ?? I18n.locale;

// Provider registry — verticals register their providers via registerAppProvider().
// Import a vertical's bootstrap.ts (side-effect import) before renderRouter() is called.
// Registration order is irrelevant: none of the providers consume another provider's context.
// TODO: each pack should import only the vertical bootstraps it actually needs;
// currently all packs load all vertical providers.
const appProviders: React.FC<{ children: React.ReactNode }>[] = [];

export function registerAppProvider(
  ...providers: React.FC<{ children: React.ReactNode }>[]
) {
  appProviders.push(...providers);
}

// children is the initial accumulator, so it ends up deepest in the tree.
const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  appProviders.reduce((acc, Provider) => <Provider>{acc}</Provider>, children);

function renderReactRoot(children: React.ReactElement, selector: string) {
  const element = document.getElementById(selector);
  invariant(element);

  const { unleashConfig, faro } = getCvPartner();

  const root = createRoot(element, {
    onUncaughtError: (error, errorInfo) => {
      faro?.api.pushError(error as Error, {
        context: { componentStack: errorInfo.componentStack ?? "" },
      });
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack },
      });
    },
    onCaughtError: (error, errorInfo) => {
      faro?.api.pushError(error as Error, {
        context: { componentStack: errorInfo.componentStack ?? "" },
      });
      Sentry.captureException(error, {
        level: "warning",
        extra: { componentStack: errorInfo.componentStack },
      });
    },
  });

  root.render(
    <React.StrictMode>
      <FaroErrorBoundary>
        <FlagProvider
          config={unleashConfig}
          startClient={unleashConfig != null}
        >
          <QueryClientProvider>
            <NothingUntilUnleashReady>
              <I18nProvider locale={locale}>
                <Providers>
                  <TimeoutWatcher />
                  {children}
                </Providers>
              </I18nProvider>
            </NothingUntilUnleashReady>
            {isDevelopment && (
              <TanStackDevtools
                config={{ openHotkey: [] }}
                plugins={[
                  {
                    name: "TanStack Query",
                    render: <ReactQueryDevtoolsPanel />,
                  },
                ]}
              />
            )}
          </QueryClientProvider>
        </FlagProvider>
        <NavigationBlocker />
      </FaroErrorBoundary>
    </React.StrictMode>,
  );

  return root;
}

export function createRouter(
  routes: RouteObject[],
  useBrowserHistoryRouter = false,
): Router {
  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(
    useBrowserHistoryRouter ? createBrowserRouter : createHashRouter,
  );

  let router = sentryCreateBrowserRouter(routes);

  const { faro } = getCvPartner();

  if (faro != null) {
    router = withFaroRouterInstrumentation(router);

    router.subscribe(() => {
      const currentHref = window.location.href;

      if (faro.api.getPage()?.url !== currentHref) {
        faro.api.setPage({
          url: currentHref,
          attributes: {
            host: window.location.host,
          },
        });
      }
    });
  }

  return router;
}

export function renderRouter(router: Router, selector: string) {
  renderReactRoot(<RouterProvider router={router} />, selector);
}
